// THE ONLY SANCTIONED WAY TO TURN A POINTER POSITION INTO A STYLE.
//
// This app renders with `zoom: 0.8` on the ROOT element, so two things
// the code freely passes numbers between are NOT the same unit:
//
//   event.clientX / getBoundingClientRect()  ->  ZOOMED (rendered) px
//   style.left / width / offsetWidth          ->  UNZOOMED (layout) px
//
// A distance measured from a rect and written to `style` therefore
// renders at 80% of where it was measured, and the error is
// PROPORTIONAL to the distance - 6 px at 30 px in, 180 px at 900 px in.
// It reads to a person as "it starts in the wrong place" (the box-select
// rectangle) or as a panel that feels sticky (the resizable divider,
// which runs away from the cursor by 25% and accumulates over a drag).
//
// A calculation is SAFE when both sides are in the same space: the
// box-select's own hit test takes the rect in client pixels AND the
// canvas size in client pixels, so the factor cancels. Percentages are
// safe too - they resolve against the parent inside the same space.
// Most of this codebase is immune for that reason, BY ACCIDENT rather
// than by choice, which is exactly why this helper exists: there is
// otherwise nothing to stop the next pointer-driven overlay
// reintroducing the bug.

// Rendered pixels per layout pixel, measured from the element itself.
//
// MEASURED, NOT READ FROM `zoom`: rendered-over-layout also captures a
// CSS transform on any ancestor, so a transform added later cannot
// defeat it. Reading the zoom property would fix only today's cause.
//
// Every failure path returns 1. A wrong scale is worse than no scale:
// unconverted is off by 20%, mis-converted can be off by any amount.
export function cssPixelScale(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return 1;
  const layout = Number(el.offsetWidth);
  if (!Number.isFinite(layout) || layout <= 0) return 1;
  const rect = el.getBoundingClientRect();
  const rendered = rect && Number(rect.width);
  if (!Number.isFinite(rendered) || rendered <= 0) return 1;
  const s = rendered / layout;
  // A pathological ratio means something is mid-layout or display:none;
  // draw unscaled rather than throw the element off the screen.
  if (!Number.isFinite(s) || s <= 0.05 || s > 20) return 1;
  return s;
}

// A client-space distance, in the units `style` is interpreted in.
// `el` is any element in the same document - the one being positioned,
// or the one the pointer was measured against.
export function clientToCssPx(distance, el) {
  const d = Number(distance);
  if (!Number.isFinite(d)) return 0;
  return d / cssPixelScale(el);
}
